// =============================================================================
// Optim TURPE V2 — LE PÉRIMÈTRE SERVI (DEV-1913, A16, lot DEV-1912).
//
// Ce que le client doit vivre : un compteur que l'étude ne couvre pas le dit
// AVANT le clic, avec sa vraie cause, au lieu de lancer une étude qui échouera.
//
// Le défaut corrigé : la page recopiait la règle d'éligibilité (« segment non
// vide », `resolveSourceSegmentForOptim`) pendant que son libellé annonçait
// « C5/C4/C2 ». Les deux divergeaient, et un C1 restait atteignable au clic
// (DEV-1678 : bascule silencieuse vers le C5).
//
// La règle tenue ici : la page ne recopie plus rien. Elle lit la liste servie par
// le moteur. Quand elle ne l'a pas, elle ne pré-annonce RIEN et dit qu'elle ne
// connaît pas le périmètre — un « couvert » présumé serait exactement le défaut
// qu'on corrige, et un « non couvert » présumé fermerait la porte à tort.
// =============================================================================

import type { OptimTurpeV2Perimetre } from "@modules/courtage/types/optim-turpe-v2"

export type VerdictSegment = {
    /** `true` couvert, `false` hors périmètre, `null` périmètre inconnu. */
    couvert: boolean | null
    /** Cause servie par le moteur pour un segment hors périmètre. */
    cause: string | null
    /** Libellé servi par le moteur. Jamais une phrase inventée par la page. */
    libelle: string | null
}

const VERDICT_INCONNU: VerdictSegment = {
    couvert: null,
    cause: null,
    libelle: null,
}

/**
 * Normalise un segment d'entrée vers son équivalent servi, avec la table
 * d'équivalences du MOTEUR (`P1 → C2`, `P3 → C4`, `P4 → C5`) et non une copie.
 */
export function segmentNormalise(
    perimetre: OptimTurpeV2Perimetre | null | undefined,
    segment: string | null | undefined
): string | null {
    if (!segment) return null
    const equivalences = perimetre?.equivalences ?? {}
    return equivalences[segment] ?? segment
}

/**
 * Le segment de ce compteur est-il couvert par l'étude ?
 *
 * Trois réponses, et la troisième compte : « je ne sais pas ». Elle survient
 * tant que la façade ne relaie pas `GET /optimturpe/perimetre`, et la page doit
 * alors laisser le client lancer son étude — le refus nommé du moteur arrivera
 * au retour, avec sa cause vraie et ses leviers.
 */
export function verdictSegment(
    perimetre: OptimTurpeV2Perimetre | null | undefined,
    segment: string | null | undefined
): VerdictSegment {
    const couverts = perimetre?.segments_couverts
    if (!Array.isArray(couverts) || couverts.length === 0) {
        return VERDICT_INCONNU
    }
    const normalise = segmentNormalise(perimetre, segment)
    if (!normalise) return VERDICT_INCONNU
    if (couverts.includes(normalise)) {
        return { couvert: true, cause: null, libelle: null }
    }
    const hors = (perimetre?.hors_perimetre ?? []).find(
        (entree) => entree.segment === normalise
    )
    // Un segment absent des couverts ET absent de la liste des hors-périmètre est
    // un segment que le moteur ne connaît pas : on ne tranche pas à sa place.
    if (!hors) return VERDICT_INCONNU
    return {
        couvert: false,
        cause: hors.cause ?? null,
        libelle: hors.libelle ?? null,
    }
}

/**
 * Les populations que l'étude ne couvre pas, quel que soit le segment (A16) —
 * producteur pur aujourd'hui. Servies par le moteur, jamais recopiées.
 */
export function populationsHorsPerimetre(
    perimetre: OptimTurpeV2Perimetre | null | undefined
): Array<{ cause: string | null; libelle: string | null }> {
    return (perimetre?.populations_hors_perimetre ?? []).map((population) => ({
        cause: population.cause ?? null,
        libelle: population.libelle ?? null,
    }))
}

/**
 * Le périmètre est-il connu ? Sert à choisir entre « dire avant le clic » et
 * « laisser lancer », sans jamais présumer d'un verdict.
 */
export function perimetreConnu(
    perimetre: OptimTurpeV2Perimetre | null | undefined
): boolean {
    return (
        Array.isArray(perimetre?.segments_couverts) &&
        (perimetre?.segments_couverts?.length ?? 0) > 0
    )
}
